import { FC, useContext, useEffect, useState } from 'react';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { GetPageProps, UserRegistrationState } from 'hooks/u002/useUserRegistrationHooks';
import { Form } from 'templates/Form';
import { Button } from 'atoms/Button';
import { User } from 'domains/User';
import { LabeledInputField } from 'molecules/LabeledInputField';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { useCreateUser } from 'hooks/u002/useCreateUserHooks';
import { PhoneNumber } from 'domains/PhoneNumber';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { CreateUserFormSchemaType } from 'commons/auditionForm';
import { useCreateUserFormSchema } from 'hooks/u002/useCreateUserFormSchema';
import { LabeledPasswordField } from 'molecules/LabeledPasswordField';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { LabelSection } from 'molecules/LabelSection';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATHS } from 'commons/paths';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppContext } from 'templates/AppContextProvider';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { formatPhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input';
import Cookies from 'js-cookie';

//U002F08 応募者登録画面
type UserRegistrationProps = GetPageProps<UserRegistrationState, 'UserRegistration'>;
export const UserRegistration: FC<UserRegistrationProps> = (props) => {
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const navigate = useNavigate();
  const [createUser, isLoadingCreateUser] = useCreateUser();
  const CreateUserFormSchema = useCreateUserFormSchema();
  const [isLoading, setIsLoading] = useState(false);
  const { currentLang, isGlobal } = useContext(AppContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('sessionId');

  const createUserFormDefaultValues: CreateUserFormSchemaType = {
    id: '',
    password: '',
    passwordConfirmation: '',
    phoneNumber: props.phoneNumber,
  };
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    clearErrors,
  } = useForm<CreateUserFormSchemaType>({
    resolver: zodResolver(CreateUserFormSchema),
    shouldFocusError: true,
    defaultValues: createUserFormDefaultValues,
  });
  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);
  const [ErrorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const onSubmit: SubmitHandler<CreateUserFormSchemaType> = async (form) => {
    setIsLoading(true);
    const user: User = new User(
      form.id,
      '',
      '',
      '',
      '',
      undefined,
      undefined,
      new Date(),
      '',
      new PhoneNumber(form.phoneNumber),
      undefined,
      undefined
    );
    console.log('request', {
      user,
      password: form.password,
      sessionId: sessionId ?? '',
      lang: currentLang,
      path: window.location.href,
    });
    await createUser({
      request: {
        user,
        password: form.password,
        sessionId: sessionId ?? '',
        lang: currentLang,
        path: window.location.origin + window.location.pathname,
      },
      onSuccess: () => {
        Cookies.remove('userBirthday');
        setIsLoading(false);
        setInfoMessage([tAuditionForm('TU002_069'), tAuditionForm('TU002_070')]);
      },
      onError: (error) => {
        console.log({ error });
        if (error instanceof Error) {
          setErrorMessage(
            error.message === 'MM002F0093E'
              ? tAuditionForm('TU002_049')
              : error.message === 'MM002F0108E'
                ? tAuditionForm('TU002_060')
                : tAuditionForm('TU002_050')
          );
          setShowErrorModal(true);
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <>
      {(isLoading || isLoadingCreateUser) && <LoadingModal />}
      {infoMessage.length > 0 && (
        <MessageModal
          message={infoMessage}
          closeLabel={tAuditionForm('TU002_071')}
          onClose={() => {
            const toParam = new URLSearchParams();
            toParam.set('redirect', window.location.pathname);
            const to = PATHS.LOGIN + '?' + toParam.toString();
            navigate(to);
          }}
        />
      )}
      {showErrorModal && (
        <MessageModal
          message={ErrorMessage}
          closeLabel={tAuditionForm('TU002_032')}
          onClose={() => setShowErrorModal(false)}
        />
      )}
      <AppLogoHeader />
      <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
        {tAuditionForm('TU002_009')}
      </div>
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tAuditionForm('TU002_009')}>
            <div>
              {isGlobal && (
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <LabelSection
                      title={tAuditionForm('TU002_012')}
                      value={formatPhoneNumberIntl(field.value) || field.value}
                      id="phoneNumber"
                    />
                  )}
                />
              )}
              {!isGlobal && (
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <LabelSection
                      title={tAuditionForm('TU002_012')}
                      value={formatPhoneNumber(field.value) || field.value}
                      id="phoneNumber"
                    />
                  )}
                />
              )}
              <FieldErrorMessages messages={[errors.phoneNumber?.message]} />
            </div>
            <div>
              <LabeledInputField
                title={tAuditionForm('TU002_016')}
                placeholder={''}
                ariaLabel={tAuditionForm('TU002_016')}
                {...register('id')}
                className="w-[25rem]"
                id="userId"
                required={true}
              />
              <FieldErrorMessages messages={[errors.id?.message]} />
            </div>
            <div>
              <LabeledPasswordField
                title={tAuditionForm('TU002_018')}
                placeholder={''}
                id="password"
                {...register('password')}
                className="w-[25rem] box-border"
                aria-label={tAuditionForm('TU002_018')}
                autoComplete="new-password"
                required={true}
              />
              <p className="text-sm text-[--c-text2] mb-2">{tAuditionForm('TU002_132')}</p>
              <FieldErrorMessages messages={[errors.password?.message]} />
            </div>
            <div>
              <LabeledPasswordField
                title={tAuditionForm('TU002_019')}
                placeholder={''}
                id="passwordConfirmation"
                {...register('passwordConfirmation')}
                className="w-[25rem]"
                aria-label={tAuditionForm('TU002_019')}
                autoComplete="new-password"
                required={true}
              />
              <FieldErrorMessages messages={[errors.passwordConfirmation?.message]} />
            </div>
            <div className={'h-[20px]'} />
            <div className={'flex justify-center items-center'}>
              <Button className="w-[10rem]" label={tAuditionForm('TU002_058')} type="submit" />
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
};
